/**
 * Sample evidence chains.
 *
 * Two, because the contrast is the whole point: one claim that traces back to
 * somebody who walked out and looked, and one that originates in a model and
 * has two links with nothing behind them at all. A mock with only the first
 * would let a screen ship that never renders a gap, which is the single thing
 * this feature exists to render.
 */
import type { Chain } from "../domain/provenance";

const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString();

export function mockChain(candidateId: string): Chain {
  if (candidateId.startsWith("P-7") || candidateId.startsWith("model")) {
    return {
      found: true,
      subject: {
        id: candidateId,
        title: "Pavement section predicted to fall below intervention PCI — PAV-014",
        status: "candidate",
        where: "PAV-014",
      },
      links: [
        {
          step: "why it is on the register",
          kind: "derived",
          what: "a model raised it: rule 'pci_decline_predictive' fired on predicted(pci) = 43.0",
          source: "pci_decline_predictive",
          concern:
            "A prediction is not an observation. Nobody has been out to look at this; a model said it would happen.",
          wouldStrengthen:
            "an inspection of the asset, or a drift check showing this model still describes the estate",
        },
        {
          step: "the model's own confidence",
          kind: "derived",
          what: "stated confidence 0.900",
          source: "pci_decline_predictive",
          concern:
            "A confidence is the model's opinion of itself and is not evidence that it is calibrated.",
          wouldStrengthen: "a drift check on this model over a window with outcomes",
        },
        {
          step: "photographic evidence",
          kind: "missing",
          what: "no photograph is attached to anything behind this candidate",
          concern: "Nothing here can be re-examined without going back to site.",
          wouldStrengthen: "a before photograph against the observation",
        },
        {
          step: "the price",
          kind: "missing",
          what: "the candidate names no treatment, so there is nothing to price",
          concern: "Every figure this candidate appears in excludes it entirely.",
          wouldStrengthen: "a unit rate for this treatment, with its source",
        },
        {
          step: "where it is",
          kind: "observed",
          what: "PAV-014 on the survey",
          source: "asset register",
        },
      ],
      counts: { derived: 2, missing: 2, observed: 1 },
      complete: false,
      gaps: [],
      weakest: {
        step: "photographic evidence",
        kind: "missing",
        what: "no photograph is attached to anything behind this candidate",
      },
      reading:
        "2 of 5 links in this chain do not exist. The claim is not wrong — it is unevidenced at those " +
        "points, and an auditor will stop at the first of them: no photograph is attached to anything " +
        "behind this candidate.",
      noScore:
        "This does not grade the claim. A chain of four observed links is not '92% reliable', and a " +
        "number like that would be the least defensible thing on the page. What is reported is the chain, " +
        "its weakest link, and what would strengthen it.",
    };
  }

  return {
    found: true,
    subject: {
      id: candidateId,
      title: "Seal TWY B, chainage 400–1300",
      status: "candidate",
      where: "TWY-B",
    },
    links: [
      {
        step: "why it is on the register",
        kind: "observed",
        what: "a defect was recorded: cracking observed along the centreline",
        source: "observation 8f2a",
        recordedBy: "Hassan Al-Otaibi (Airfield inspector)",
        recordedAt: daysAgo(24),
        ageDays: 24,
      },
      {
        step: "photographic evidence",
        kind: "observed",
        what: "3 photograph(s) on the observation behind this candidate (before)",
        source: "evidence 41c9",
        concern:
          "The photographs hang off the record behind this candidate, not off the candidate itself.",
        recordedAt: daysAgo(24),
        ageDays: 24,
      },
      {
        step: "the price",
        kind: "asserted",
        what: "48.00 SAR per m for 'crack_sealing'",
        source: "2022 term contract",
        recordedAt: daysAgo(900),
        ageDays: 900,
        concern:
          "This rate was priced 900 days ago. Whether the contract behind it still stands is not " +
          "something this platform knows.",
        wouldStrengthen: "a re-priced rate, or confirmation the term contract is still live",
      },
      {
        step: "the cost of waiting",
        kind: "asserted",
        what: "deferral factor 1.600 per year",
        source: "ASTM D5340 progression; sealing untreated becomes patching in two winters",
      },
      {
        step: "where it is",
        kind: "observed",
        what: "TWY-B on the survey",
        source: "asset register",
      },
    ],
    counts: { observed: 3, asserted: 2 },
    complete: true,
    gaps: [],
    weakest: {
      step: "the price",
      kind: "asserted",
      what: "48.00 SAR per m for 'crack_sealing'",
      ageDays: 900,
      concern:
        "This rate was priced 900 days ago. Whether the contract behind it still stands is not " +
        "something this platform knows.",
    },
    reading:
      "Every link exists. The chain is limited by what somebody asserted rather than observed: " +
      "48.00 SAR per m for 'crack_sealing'.",
    noScore:
      "This does not grade the claim. A chain of four observed links is not '92% reliable', and a " +
      "number like that would be the least defensible thing on the page. What is reported is the chain, " +
      "its weakest link, and what would strengthen it.",
  };
}
